import { useEffect, useState } from "react";
import { HealthMatchIcon } from "../icons/HealthMatchIcon";
import { LandingPageConfig } from "../types/landingPage";
import { LandingPageLayout } from "../components/LandingPageLayout";
import { useQuestionnaireQueryStates } from "../hooks/useQueryParams";
import { captureMessage } from "@sentry/react";
import { track } from "../utils/rudderstack";
import {
  mergePartnerConfig,
  usePartnerConfig,
} from "../hooks/usePartnerConfig";
import { MessagesSquare } from "lucide-react";
import { BPUploadSection } from "../components/bp-upload/BPUploadSection";

export function ThankYouPageContent({ config }: { config: LandingPageConfig }) {
  const [{ formData, apiResponse }] = useQuestionnaireQueryStates();
  const partnerConfig = usePartnerConfig();
  const [showContinueOnlineLink, setShowContinueOnlineLink] = useState(false);

  const bodyText =
    config.thankYouPage?.bodyText ||
    "Thank you for your submission. We'll be in touch soon.";

  useEffect(() => {
    if (!formData || !apiResponse) {
      captureMessage(
        "Thank you page loaded without form data or API response",
        {
          level: "error",
          tags: {
            landing_page_id: config.id,
            error_type: "missing_form_data",
          },
        },
      );
      window.location.href = `/${config.id}`;
      return;
    }

    // Track thank you page view
    track("lp.thank_you_page.viewed", {
      landing_page_id: config.id,
    });
  }, [config.id, formData, apiResponse]);

  useEffect(() => {
    // Show the continue link after 7 seconds
    const timer = setTimeout(() => {
      setShowContinueOnlineLink(true);
    }, 7000);

    return () => clearTimeout(timer);
  }, []);

  return (
    <LandingPageLayout
      title={config.title}
      description={config.description}
      {...mergePartnerConfig(partnerConfig, config)}
    >
      <div className="relative bg-white" data-testid="thank-you-page">
        <div className="absolute inset-0 overflow-hidden pointer-events-none hidden md:block">
          <div className="absolute top-32 right-0 w-[200px] h-[200px] md:w-[350px] md:h-[350px] text-primary-500 floating-orb">
            <HealthMatchIcon />
          </div>

          <div className="absolute top-48 -left-16 w-[180px] h-[180px] md:w-[250px] md:h-[250px] text-primary-500 floating-orb-reverse">
            <HealthMatchIcon />
          </div>

          <div className="absolute bottom-48 right-8 w-[120px] h-[120px] md:right-32 md:w-[180px] md:h-[180px] text-primary-500 floating-orb-pulse">
            <HealthMatchIcon />
          </div>
        </div>

        <div
          className="relative z-10 isolation max-w-7xl mx-auto px-2 sm:px-3 lg:px-4 pt-4 md:pt-8 pb-4 md:pb-20"
          data-testid="thank-you-content"
        >
          <div className="text-center">
            <div
              className="text-[1.625rem] leading-[1.3] md:leading-[1.2] tracking-tight font-bold text-gray-900 sm:text-3xl md:text-4xl px-2 sm:px-4"
              data-testid="thank-you-title"
            >
              <span className="block mb-3 md:mb-4">
                Keep an eye on your phone!
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 md:gap-8">
            <div className="w-full max-w-4xl mx-auto px-2">
              <div
                className="bg-white rounded-2xl overflow-hidden p-8 flex flex-col items-center justify-center text-center"
                data-testid="thank-you-card"
              >
                <div className="mb-6 text-primary-500">
                  <MessagesSquare
                    className="h-16 w-16 mx-auto"
                    strokeWidth={2}
                  />
                </div>

                <p className="text-lg text-gray-700 mb-6 max-w-2xl">
                  {bodyText}
                </p>

                {showContinueOnlineLink && (
                  <p className="text-sm text-gray-600">
                    Didn't receive a text?{" "}
                    <a
                      href="https://app.healthmatch.io/signup/97?ftid=f25b39d8-f5ec-4eb9-a826-5d27240f99df"
                      className="text-primary-600 hover:text-primary-700 underline font-medium"
                      onClick={() => {
                        track("lp.thank_you_page.continue_online_clicked", {
                          landing_page_id: config.id,
                        });
                      }}
                    >
                      Continue online
                    </a>
                  </p>
                )}
              </div>
            </div>
          </div>

          {config.bpUpload &&
            apiResponse?.outcome.patientId != null && (
              <BPUploadSection
                landingPageId={config.id}
                bpUpload={config.bpUpload}
                patient={{
                  patientId: apiResponse.outcome.patientId,
                  applicationSiteId: apiResponse.outcome.applicationSiteId ?? null,
                }}
              />
            )}
        </div>
      </div>
    </LandingPageLayout>
  );
}
